import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Create = lazy(() => import("./pages/Environments/Create"));
const CreateDetails = lazy(() => import("./pages/Environments/CreateDetails"));
const Active = lazy(() => import("./pages/Environments/Active"));
const Details = lazy(() => import("./pages/Environments/Details"));
const EnvironmentHistory = lazy(() => import("./pages/Environments/History"));
const DbDumps = lazy(() => import("./pages/Resources/DbDumps"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const RepoPullRequests = lazy(
  () => import("./pages/CodeHub/PullRequests/RepoPullRequests"),
);
const PRDetails = lazy(() => import("./pages/CodeHub/PRDetails"));
const NotFoundRedirect = lazy(
  () => import("./components/global/NotFoundRedirect"),
);
const DocsHome = lazy(() => import("./pages/Documentation/DocsHome"));
const DocPage = lazy(() => import("./pages/Documentation/DocPage"));
const ShortcutStoryDetails = lazy(
  () => import("./pages/CodeHub/ShortcutStoryDetails"),
);
const GithubAppLogs = lazy(() => import("./pages/Logs"));
const Status = lazy(() => import("./pages/Status"));
const SettingsVariables = lazy(() => import("./pages/Settings/Variables"));
const SettingsServiceVariables = lazy(() => import("./pages/Settings/ServiceVariables"));
const CronJobs = lazy(() => import("./pages/CronJobs"));
const CronJobDetails = lazy(() => import("./pages/CronJobs/CronJobDetails"));
const EnvironmentDetails = lazy(
  () => import("./pages/Environments/EnvironmentDetails"),
);
const CreatePR = lazy(() => import("./pages/CodeHub/CreatePR"));
const PullRequests = lazy(() => import("./pages/CodeHub/PullRequests"));
const SidebarLayout = lazy(() => import("./layouts/SidebarLayout"));
const ProtectedRoute = lazy(() => import("./components/auth/ProtectedRoute"));
const BaseLogin = lazy(() => import("./pages/CodeHub/BaseLogin"));

const ProtectedShell = () => (
  <ProtectedRoute>
    <SidebarLayout />
  </ProtectedRoute>
);

const CODE_HUB = [
  {
    path: "active-prs",
    name: "Active Pull Requests",
    component: PullRequests,
  },
  {
    path: "create-pr",
    name: "Create Pull Request",
    component: CreatePR,
  },
];

const LOG_PAGES = [
  { path: "github-app", name: "Github App Logs", component: GithubAppLogs },
];

const SETTINGS_PAGES = [
  { path: "variables", name: "Variables", component: SettingsVariables },
  { path: "service-variables", name: "Service Variables", component: SettingsServiceVariables },
];

const Router = () => {
  return (
    <Suspense fallback={<div>Loading…</div>}>
      <Routes>
        <Route path="/login" element={<Login />} />

        <Route path="/" element={<ProtectedShell />}>
          <Route index element={<Navigate to="/dashboard" replace />} />

          <Route path="dashboard" element={<Dashboard />} />

          <Route path="status" element={<Status />} />

          <Route path="environments">
            <Route path="create" element={<Create />} />
            <Route path="create/details" element={<CreateDetails />} />

            <Route path="active" element={<Active />} />
            <Route path="active/details" element={<Details />}>
              <Route path=":identifier" element={<EnvironmentDetails />} />
            </Route>

            <Route path="history" element={<EnvironmentHistory />} />
          </Route>

          <Route path="resources">
            <Route path="db_dumps" element={<DbDumps />} />
          </Route>

          <Route path="docs">
            <Route index element={<DocsHome />} />
            <Route path="*" element={<DocPage />} />
          </Route>

          <Route path="cron-jobs">
            <Route index element={<Navigate to="development" replace />} />
            <Route path=":environment" element={<CronJobs />} />
            <Route path=":environment/:id" element={<CronJobDetails />} />
          </Route>

          <Route
            path="testing-suite"
            element={<ComingSoon pageName="Test Suite" />}
          />
          <Route path="codehub">
            <Route path="github" element={<BaseLogin />} />
            <Route path="active-prs" element={<PullRequests />}>
              <Route path=":repo" element={<RepoPullRequests />} />

              <Route
                path=":repo/:prId"
                element={
                  <Suspense fallback={<div>Loading…</div>}>
                    <PRDetails />
                  </Suspense>
                }
              />
            </Route>

            <Route
              path="active-prs/stories/:storyId"
              element={
                <Suspense fallback={<div>Loading…</div>}>
                  <ShortcutStoryDetails />
                </Suspense>
              }
            />

            {CODE_HUB.filter((p) => p.path !== "active-prs").map(
              ({ path, name, component: Comp }) => (
                <Route
                  key={path}
                  path={path}
                  element={Comp ? <Comp /> : <ComingSoon pageName={name} />}
                />
              ),
            )}
          </Route>

          <Route path="logs">
            {LOG_PAGES.map(({ path, name, component: Comp }) => (
              <Route
                key={path}
                path={path}
                element={Comp ? <Comp /> : <ComingSoon pageName={name} />}
              />
            ))}
          </Route>

          <Route path="settings">
            {SETTINGS_PAGES.map(({ path, name, component: Comp }) => (
              <Route
                key={path}
                path={path}
                element={Comp ? <Comp /> : <ComingSoon pageName={name} />}
              />
            ))}
          </Route>
        </Route>

        <Route path="*" element={<NotFoundRedirect />} />
      </Routes>
    </Suspense>
  );
};

export default Router;
